<template>
  <MapBoxPopup
    :coordinates="coordinatesType"
    :offset="[0, 0]"
    :show="show"
    :anchor="popupAnchor"
    @close="handleClose"
  >
    <div class="Popup--TwoStepGeocoding">
      <!-- needs to be placed in a global manner for the popup, so it's not removed when in editing mode -->
      <MapLineWithDistance
        v-if="!hasParkinglotLayer"
        :from-coordinates="coordinates"
        :to-points="[geocodedCoordinates]"
      />

      <!-- Loading -->
      <Step v-if="isLoading">
        <div class="d-flex align-items-center">
          Loading...
          <b-spinner
            variant="primary"
            small
          />
        </div>
      </Step>

      <!-- Step 1 -->
      <Step
        v-else-if="step === 1"
        :title="isAlternativeLocation ? 'Alternatieve locatie' : $t('newLocation')
        "
      >
        <!-- Close button -->
        <button
          v-if="hasParkinglotLayer"
          class="mapboxgl-popup-close-button parkinglot-popup"
          type="button"
          aria-label="Close popup"
          @click="handleCancelParkinglots"
        >
          <i class="far fa-times" />
        </button>

        <Form
          class="p-2"
          @submit.prevent="fetchAddress"
        >
          <!-- Remarks -->
          <FormField
            v-model="fields.remark.value"
            v-bind="fields.remark"
            :rows="3"
            class="mb-2"
          />

          <!-- Phase (stacked) -->
          <FormField
            v-model="fields.phase.value"
            v-bind="fields.phase"
            :options="phases"
            style="pointer-events: auto"
            class="mb-2"
          />

          <!-- Status (stacked) -->
          <FormField
            v-model="fields.status.value"
            v-bind="fields.status"
            :options="statusOptions"
            class="mb-2"
            @input="setChargingPointCategory"
          />

          <!-- Charging points group -->
          <div
            v-if="groupLayers.length > 1"
            class="mb-2"
          >
            <CollapsableSegment
              v-if="hasParkinglotLayer"
              :label="chargingPointLabel"
              :visible="collapseActive"
              class="status-category"
            >
              <ChargingpointsLayerGroup
                :layer="layer"
                :group-layers="groupLayers"
                :selected-status="selectedStatus"
                :is-legend="false"
                @input="handleInput"
              />
            </CollapsableSegment>

            <div
              v-else
              class="text-center mb-2"
            >
              <strong class="d-block mb-1">{{ chargingPointLabel }}</strong>
              <ChargingpointsLayerGroup
                :layer="layer"
                :group-layers="groupLayers"
                :selected-status="selectedStatus"
                :is-legend="false"
                @input="handleInput"
              />
            </div>
          </div>

          <!-- Accessibility toggle -->
          <div
            v-if="selectedStatus.includes('private')"
            class="mb-2"
          >
            <div class="d-flex align-items-center justify-content-between mb-1">
              <div class="d-flex align-items-center gap-2">
                <b>
                  {{
                    $t(
                      "components.map.ChargerTwoStepGeocodingPopup.sharePublic"
                    )
                  }}
                </b>
                <SvgIcon
                  id="sharePublic"
                  class="tooltip-icon ml-1"
                  icon="info-circle-regular"
                />
                <b-tooltip
                  target="sharePublic"
                  triggers="hover"
                >
                  <p class="mb-0">
                    {{
                      $t(
                        "components.map.ChargerTwoStepGeocodingPopup.publicTooltip"
                      )
                    }}
                  </p>
                </b-tooltip>
              </div>

              <ToggleSwitch
                :value="publishToggle"
                @input="publishToggle = !publishToggle"
              />
            </div>

            <FormField
              v-model="fields.publish_allowed_to.value"
              v-bind="fields.publish_allowed_to"
              :disabled="networkOptions.length === 0 || publishToggle === true"
              :options="networkOptions"
            />
          </div>

          <!-- CPO -->
          <div>
            <span class="font-weight-bold"> {{ $t('cpo') }} </span>
            <CPOSelect
              v-model="currentCPO.value"
              :options="cpoOptions"
              :reduce="(option) => option.value"
              :frequently-selected-count="10"
              class="mb-2"
            />
          </div>
          <!-- Parking lot -->
          <ParkingLot
            v-if="hasParkinglotLayer"
            :coordinates="coordinatesType"
            class="mb-2"
            @parkinglotIds="(ids) => (fields.parkinglotIds.values = ids)"
            @setCoordinates="$emit('setCoordinates', $event)"
          />

          <!-- Alternative location -->
          <AlternativeLocation
            v-if="isAlternativeLocation"
            :predecessor="fields.predecessor.value"
            class="mb-2"
            @select="(value) => (fields.predecessor.value = value)"
          />

          <!-- Anchor tool -->
          <div
            v-if="hasParkinglotLayer"
            class="anchor-tool mb-2"
          >
            <component
              :is="`b-icon-arrow-${icon.type}-circle${popupAnchor === icon.position ? '-fill' : ''
              }`"
              v-for="icon in anchorIcons"
              :key="icon.type"
              class="me-2"
              @click="handleAnchor({ position: icon.position })"
            />
          </div>

          <!-- Submit button -->
          <div class="d-flex justify-content-end mt-2">
            <b-button
              type="submit"
              size="sm"
              variant="primary"
              :disabled="isDisabled"
            >
              {{ $t("components.map.ChargerTwoStepGeocodingPopup.next") }}
            </b-button>
          </div>
        </Form>
      </Step>

      <!-- Step 2 -->
      <Step
        v-else-if="step === 2"
        :title="hasAddress ? 'Adres bevestigen' : 'Voer het adres handmatig in'"
        :has-address="hasAddress"
      >
        <template #default>
          <b-alert
            variant="info"
            :show="invalidPostalCode"
          >
            {{
              $t(
                "components.map.ChargerTwoStepGeocodingPopup.invalidPostalCode"
              )
            }}
          </b-alert>
          <div v-if="isEditing">
            <Form>
              <FormField
                v-model.trim="fields.address.street.value"
                v-bind="fields.address.street"
              />
              <div class="row">
                <div class="col-5">
                  <FormField
                    v-model.trim="fields.address.number.value"
                    v-bind="fields.address.number"
                    type="number"
                  />
                </div>
                <div class="col-4">
                  <FormField
                    v-model.trim="fields.address.suffix.value"
                    v-bind="fields.address.suffix"
                  />
                </div>
              </div>

              <div class="row">
                <div class="col-5">
                  <FormField
                    v-model.trim="fields.address.postalcode.value"
                    v-bind="fields.address.postalcode"
                  />
                </div>
                <div class="col-7">
                  <FormField
                    v-model.trim="fields.address.city.value"
                    v-bind="fields.address.city"
                  />
                </div>
              </div>
            </Form>
          </div>

          <div v-else>
            <button
              v-if="hasParkinglotLayer"
              class="mapboxgl-popup-close-button parkinglot-popup"
              type="button"
              aria-label="Close popup"
              aria-hidden="true"
              @click="handleCancelParkinglots"
            >
              <i class="far fa-times" />
            </button>
            <strong>{{
              $t("components.map.ChargerTwoStepGeocodingPopup.address")
            }}</strong>
            <p class="mb-2">
              {{ address.formatted_address }}
            </p>

            <strong>{{
              $t("components.map.ChargerTwoStepGeocodingPopup.addressSource")
            }}</strong>
            <p v-if="address.source === SOURCE.MANUAL">
              <span class="text-success">{{ sourceSlug }}</span>
            </p>

            <p v-if="address.source !== SOURCE.MANUAL">
              {{ sourceSlug }} <br>
              {{ $t("components.map.ChargerTwoStepGeocodingPopup.distance") }}:
              <span :class="distanceIndicationClassName">{{ distance }}</span>
            </p>
          </div>
        </template>

        <template #footer>
          <template v-if="isEditing">
            <b-button
              size="sm"
              variant="danger"
              @click="handleCancel"
            >
              {{ $t("cancel") }}
            </b-button>

            <b-button
              size="sm"
              class="ml-1"
              variant="primary"
              :disabled="isSubmitting"
              @click="handleUpdateAddress"
            >
              {{ $t("update") }}
            </b-button>
          </template>

          <template v-else>
            <b-button
              size="sm"
              variant="danger"
              @click="isEditing = true"
            >
              {{ $t("edit") }}
            </b-button>

            <b-button
              size="sm"
              class="ml-1"
              variant="primary"
              :disabled="invalidPostalCode || isSubmitting"
              @click="handleSubmit"
            >
              <b-spinner
                v-if="isSubmitting"
                small
              />
              <span v-else>{{ isUpdate ? $t("confirm") : $t("add") }}</span>
            </b-button>
          </template>
        </template>
      </Step>
    </div>
  </MapBoxPopup>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

// components
import MapBoxPopup from '@/components/common/MapBoxPopup'
import ToggleSwitch from '@/components/form/ToggleSwitch'
import MapLineWithDistance from '@/components/map/MapLineWithDistance'
import FormField from '@/components/form/FormField'
import Form from '@/components/form/Form'
import Step from '@/components/common/Step'
import AlternativeLocation from '@/components/map/sidebar/AlternativeLocation'
import ChargingpointsLayerGroup from '@/components/common/ChargingpointsLayerGroup.vue'
import ParkingLot from '@/components/common/ParkingLot'
import CollapsableSegment from '@/components/common/CollapsableSegment'
import {
  BIconArrowLeftCircle,
  BIconArrowRightCircle,
  BIconArrowUpCircle,
  BIconArrowDownCircle,
  BIconArrowLeftCircleFill,
  BIconArrowRightCircleFill,
  BIconArrowUpCircleFill,
  BIconArrowDownCircleFill,
} from 'bootstrap-vue'
import SvgIcon from '@/components/common/SvgIcon'

// mixins
import chargingpointCpoMixin from '@/mixins/chargingpoint/chargingpointCpoMixin'
import statusMixin from '@/mixins/common/statusMixin'
import mapBoxMixin from '@/components/common/MapBoxMixin'
import chargingpointEditMixin from '@/mixins/chargingpoint/chargingpointEditMixin'

// services
import { SOURCE, getSourceLabel } from '@/services/sourceTranslation'
import { turf } from '@/services/turf'
import { createMarker } from '@/helpers/mapbox'
import { provincieCodeByMunicipalityCode } from '@/services/municipalities'

// shared
import { dynamicReverseGeocode } from '@/../shared/services/geocoder'
import { createChargingPoint } from '@/../shared/API/chargingpoints'
import { insertComment } from '@/../shared/API/comments'
import { isValidPostalcode } from '@/services/validation'

import { distanceBetweenTwoPoints } from '@/../shared/helpers/measuring'
import {
  formatFormattedAddress,
  formatSimpleAddress,
} from '@/../shared/helpers/address'
import { ADDRESS_QUALITY_DISTANCE } from '@/../shared/valueholders/address-quality-distance'

import getCssProperty from '@/helpers/cssProperty'
import vSelect from '@/components/form/vSelect'
import CPOSelect from '@/components/form/CPOSelect'

// locale values
const START_STEP = 1

export default {
  name: 'ChargerTwoStepGeocodingPopup',
  components: {
    Step,
    CPOSelect,
    SvgIcon,
    MapBoxPopup,
    vSelect,
    Form,
    FormField,
    MapLineWithDistance,
    AlternativeLocation,
    ChargingpointsLayerGroup,
    ParkingLot,
    BIconArrowLeftCircle,
    BIconArrowRightCircle,
    BIconArrowUpCircle,
    BIconArrowDownCircle,
    BIconArrowLeftCircleFill,
    BIconArrowRightCircleFill,
    BIconArrowUpCircleFill,
    BIconArrowDownCircleFill,
    CollapsableSegment,
    ToggleSwitch,
  },
  mixins: [
    mapBoxMixin,
    chargingpointCpoMixin,
    statusMixin,
    chargingpointEditMixin,
  ],
  props: {
    coordinates: {
      type: Array,
      required: true,
    },
    isUpdate: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    firstSelectedParkingCoordinates: {
      type: Array,
      default: () => [],
    },
    popupAnchor: {
      type: String,
      default: 'left',
    },
  },
  data() {
    return {
      // state
      isSubmitting: false,
      publishToggle: true,
      show: true,
      isLoading: false,
      isEditing: false,

      // helpers for two-step creation of a chargingpoint
      step: START_STEP,
      distance: null,
      networks: [],
      // this is needed to handle the close event when the popup is re-created when the anchor changes
      anchor: 'left',
      keepAlive: false,
      braliMunicipalitiesNoPreselectedCpo: [
        '0866',
        '0766',
        '1655',
        '0809',
        '1706',
        '1959',
      ],

      // start value fields
      fields: {
        publish_allowed_to: {
          label: this.$i18n.t('private_network'),
          value: [],
          type: 'select',
        },
        address: {
          street: {
            label: this.$i18n.t('address.street'),
            value: null,
          },
          number: {
            label: this.$i18n.t('address.houseNumber'),
            value: null,
          },
          suffix: {
            label: this.$i18n.t('address.suffix'),
            value: null,
          },
          postalcode: {
            label: this.$i18n.t('address.zipCode'),
            value: null,
          },
          city: {
            label: this.$i18n.t('address.city'),
            value: null,
          },
        },
        remark: {
          label: this.$i18n.t('remark'),
          value: '',
          type: 'textarea',
        },
        status: {
          label: this.$i18n.t('mapLayersPanel.status.title'),
          value: 'suggestion',
          type: 'select',
        },
        phase: {
          label: this.$i18n.t('phase'),
          value: null,
          type: 'select',
        },
        predecessor: {
          label: this.$i18n.t(
            'components.sidebar.LocatieInformatieContent.alternative',
          ),
          value: null,
        },
        parkinglotIds: {
          label: this.$i18n.t(
            'mapLayersPanel.municipality.parkingSpacesAmsterdam.title',
          ),
          values: [],
        },
      },

      // address and coordinates that will be used for creation of the location
      address: null,
      phases: [],
      isVlaanderen: process.env.VUE_APP_DEPLOYMENT === 'be',
      geocodedCoordinates: null,
      selectedStatus: 'chargingpoints-suggestion',
      chargingPointLabel: 'Voorgesteld',
      anchorIcons: [
        { type: 'left', position: 'left' },
        { type: 'up', position: 'top' },
        { type: 'right', position: 'right' },
        { type: 'down', position: 'bottom' },
      ],
      collapseActive: false,
      currentCPO: { label: '', value: null },
    }
  },
  computed: {
    ...mapGetters('access', ['getActiveMunicipality', 'getPrivateNetworks']),
    ...mapGetters('planmode', [
      'getRejectedLocationUuid',
      'getPlanmodeLayers',
      'getTempChargerPopupFields',
    ]),
    ...mapGetters('config', [
      'getPhases',
      'getDefaultValidationSettingProfile',
    ]),
    ...mapGetters('currentUser', {
      currentUserRole: 'getCurrentUserRole',
      currentUserEmail: 'getCurrentUserEmail',
      currentUserId: 'getCurrentUserId',
      currentUserName: 'getCurrentUserName',
    }),
    isBraLiMunicipality() {
      return [30, 31].includes(
        provincieCodeByMunicipalityCode({ code: this.getActiveMunicipality }),
      )
    },
    hasParkinglotLayer() {
      return !!this.$store.map.getLayer('parkinglot')
    },
    sourceSlug() {
      return getSourceLabel(this.address.source)
    },
    isUserCpo() {
      return this.currentUserRole === 'cpo'
    },
    identity() {
      return this.$auth.user['https://evmaps.nl/identity']
    },
    distanceIndicationBootstrapColor() {
      if (this.distance <= ADDRESS_QUALITY_DISTANCE.CORRECT) return 'success'
      if (this.distance <= ADDRESS_QUALITY_DISTANCE.UNCERTAIN) return 'warning'
      return 'danger'
    },
    distanceIndicationClassName() {
      return `text-${this.distanceIndicationBootstrapColor}`
    },
    isAlternativeLocation() {
      return !!this.fields.predecessor.value
    },
    groupLayers() {
      return this.getPlanmodeLayers?.filter(
        (layer) => layer.config.statusGroup === this.fields.status.value,
      )
    },
    layer() {
      return this.groupLayers[0]
    },
    chargingPointLabelStyle() {
      return `border-bottom: 1px solid ${this.layer.config.iconColor}`
    },
    hasAddress() {
      const address = Object.keys(this.fields.address).filter(
        (key) => key !== 'suffix',
      )
      return address.every((key) => !!this.fields.address[key].value)
    },
    isDisabled() {
      return (
        this.hasParkinglotLayer &&
        !this.fields.parkinglotIds.values.some((v) => !!v)
      )
    },
    coordinatesType() {
      return this.hasParkinglotLayer
        ? this.firstSelectedParkingCoordinates
        : this.coordinates
    },
    canUseB3Phase() {
      return (
        !this.identity?.uuid ||
        this.identity.category !== 'cpo' ||
        !this.isUserCpo ||
        this.identity?.name === 'Vattenfall'
      )
    },
    networkOptions() {
      return this.networks.map((holder) => ({
        value: holder.uuid,
        text: holder.name,
      }))
    },
    invalidPostalCode() {
      return (
        !!this.address.postalcode && !isValidPostalcode(this.address.postalcode)
      )
    },
    cpoOptions() {
      const priorityOrder = [
        'Equans',
        'TotalEnergies',
        'Vattenfall',
        'Park&Charge',
        'OPCHARGE',
        'Ubitricity',
        'Den Hartog Charging',
        'Dutch Charge',
        'Orange Charging',
        'Mick Energy',
      ]

      const options = [...this.cpoSelectOptions]

      const nullOption = options.find(o => o.value === null)
      const rest = options.filter(o => o.value !== null)

      rest.sort((a, b) => {
        const indexA = priorityOrder.indexOf(a.label)
        const indexB = priorityOrder.indexOf(b.label)

        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB
        }

        if (indexA !== -1) return -1
        if (indexB !== -1) return 1

        return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
      })

      return nullOption ? [nullOption, ...rest] : rest
    },

  },
  watch: {
    fields: {
      deep: true,
      handler(fields) {
        this.$store.dispatch('planmode/setTempChargerPopupFields', { fields })
      },
    },
  },
  async created() {
    if (this.getTempChargerPopupFields) {
      this.fields = this.getTempChargerPopupFields
    }
    // const token = await this.$auth.getTokenSilently()
    this.networks = this.getPrivateNetworks

    this.fields.predecessor.value = this.getRejectedLocationUuid
      ? { uuid: this.getRejectedLocationUuid }
      : null

    this.SOURCE = SOURCE

    this.step = START_STEP

    this.configurePhases()

    if (this.isUserCpo) {
      this.currentCPO.value = this.cpoSelectOptions[0].value
    }

    if (this.isUpdate) {
      await this.fetchAddress()
    }
  },
  methods: {
    ...mapActions('planmode', ['addOrUpdateChargingPoint', 'setParkingLotIds']),
    filterPhases(phase) {
      return (
        phase.active &&
        (!this.canUseB3Phase ? !phase.label.includes('B3') : true)
      )
    },
    configurePhases() {
      const oldPhases = [
        { value: 1, label: 'Fase 1' },
        { value: 2, label: 'Fase 2' },
      ]
      const newActivePhases =
        this.getPhases
          ?.filter(this.filterPhases)
          .map(({ uuid, label }) => ({ value: uuid, label })) ?? []

      const activePhases = [...oldPhases, ...newActivePhases]

      activePhases.forEach((phase) => {
        this.phases.push({ value: phase.value, text: phase.label })
      })

      if (this.isBraLiMunicipality) {
        // Some Brali municipalities are excluded from preselecting Vattenfall //
        const noPreselectedCpo =
          this.braliMunicipalitiesNoPreselectedCpo.includes(
            this.getActiveMunicipality,
          )
        if (this.canUseB3Phase) {
          this.fields.phase.value = activePhases.find(
            (phase) => phase.label === 'Fase B3',
          )?.value
        }

        this.currentCPO.value = !noPreselectedCpo
          ? this.cpoSelectOptions.find((cpo) => cpo.label === 'Vattenfall')
            ?.value
          : null
      }
    },
    /**
     * handle step 1
     */
    async fetchAddress() {
      this.isLoading = true

      this.address = await dynamicReverseGeocode({
        lng: this.coordinates[0],
        lat: this.coordinates[1],
        bilingual: process.env.VUE_APP_DEPLOYMENT === 'be',
      }).catch((e) => {
        if (e === 'not found') {
          // Geocoder found no address at the selected location, we switch to manual input //
          return
        }

        this.$notify({
          type: 'error',
          title: 'Adres ophalen niet mogelijk!',
          text: 'Er is geen connectie met de geocoder API. Probeer het later opnieuw.',
        })
      })

      let geocodedCoordinates

      if (this.address) {
        // prepare already for editing
        this.fields.address.street.value = this.address.street

        // if the address number is not a number, we need to parse it
        if (Number.isNaN(Number(this.address.number))) {
          const regex = /^([1-9][0-9]*)\s*([a-z1-9\-/]+)?$/i
          const match = this.address.number.match(regex)
          if (match) {
            this.address.number = match[1]
            this.address.suffix = match[2] || ''
          }
        }

        this.fields.address.number.value = this.address.number
        this.fields.address.suffix.value = this.address?.suffix || ''

        this.fields.address.postalcode.value = this.address.postalcode
        this.fields.address.city.value = this.address.city

        // calculate distance
        if (this.hasParkinglotLayer) {
          geocodedCoordinates = this.firstSelectedParkingCoordinates
        } else {
          geocodedCoordinates = [
            this.address.coordinates.lng(),
            this.address.coordinates.lat(),
          ]
          this.distance = distanceBetweenTwoPoints(
            this.coordinates,
            geocodedCoordinates,
          ).toFixed(2)
        }
      } else {
        // No address found, initialize address and continue with manual input //
        this.address = {}
        this.isEditing = true

        geocodedCoordinates = this.hasParkinglotLayer
          ? this.firstSelectedParkingCoordinates
          : [this.coordinates[0], this.coordinates[1]]
      }

      // prepare popup before showing the result, otherwise it will handle a close when the anchor changes, since
      // that'll remove and recreate the popup. By that we will lose the geocoded coordinates due to handleClose
      this.setAnchor({ geocodedCoordinates })

      // wait for next tick so everything is updated
      this.$nextTick(async () => {
        await this.displayResult({ geocodedCoordinates })
      })

      this.isLoading = false
    },

    setChargingPointCategory() {
      this.selectedStatus = this.layer.id
      this.chargingPointLabel = this.layer.label
    },

    handleInput(chargingPoint) {
      this.selectedStatus = chargingPoint.layer.id
      this.chargingPointLabel = chargingPoint.layer.label
    },

    handleCancelParkinglots() {
      this.$store.dispatch('planmode/setParkingLotIds', { ids: [] })
      this.$store.dispatch('planmode/setTempChargerPopupFields', {
        fields: null,
      })
      this.fields.parkinglotIds.values = []
      this.$emit('setCoordinates', null)
      this.$emit('setAnchor', { position: 'left' })
      this.handleClose()
    },

    handleAnchor({ position }) {
      this.keepAlive = true
      this.anchor = position
      this.$emit('setAnchor', { position })
    },

    /**
     * handle step 2
     */
    async handleSubmit() {
      try {
        this.isSubmitting = true

        const networkUUID = this.fields.publish_allowed_to.value

        const publish_allowed_to =
          this.publishToggle === false && networkUUID ? [networkUUID] : []

        const currentCPO = await this.$_chargingpointEditMixin_updateStakeholders(
          { stakeholderUUID: this.currentCPO.value, stakeholderType: 'cpo' },
        )

        if (this.isUpdate) {
          this.$emit('update', {
            address: this.address,
            cpo: this.currentCPO.value,
          })
        } else {
          const status =
            this.selectedStatus.replace('chargingpoints-', '') || 'suggestion'
          const coordinates = this.coordinates
          const phase = [this.fields.phase.value]

          let beheerder
          let validators
          if (status.includes('suggestion')) {
            const settings = this.getDefaultValidationSettingProfile

            beheerder = settings.beheerder
            validators = settings.validators
          }

          const payload = {
            code: this.getActiveMunicipality,
            ...(currentCPO ? { cpo: currentCPO.uuid } : {}),
            status,
            lng: coordinates[0],
            lat: coordinates[1],
            address: this.address,
            remark: this.fields.remark.value,
            parkinglotIds: this.fields.parkinglotIds.values.filter(Boolean),
            publish: this.publishToggle,
            publish_allowed_to,
            ...(phase.filter(Boolean).length ? { phase } : []),
            user: {
              name: this.currentUserName,
              id: this.currentUserId,
            },
            beheerder,
            validators,
            dataSource: {
              value: SOURCE.EVMAPS,
              label: getSourceLabel(SOURCE.EVMAPS),
            },
          }

          const chargingpoint = await createChargingPoint(payload)

          const locationCreationComment = {
            chargingPointUUID: chargingpoint.uuid,
            message: this.$i18n.t('components.sidebar.TheComments.commentMessages.chargingpoint_created', 'en-GB', { name: this.currentUserName }),
            message_type: 'chargingpoint_created',
            comment_type: 'system',
            user: { name: this.currentUserName, id: this.currentUserId },
          }
          await insertComment({ chargingPointUUID: chargingpoint.uuid, payload: locationCreationComment })

          if (validators?.find(validator => validator.user_id === this.currentUserId)) {
            const autoApproveComment = {
              message_type: 'chargingpoint_created_and_approved',
              message: this.$i18n.t('components.sidebar.TheComments.commentMessages.chargingpoint_created_and_approved', 'en-GB', { name: this.currentUserName }),
              comment_type: 'system',
              user: { name: this.currentUserName, id: this.currentUserId },
            }

            await insertComment({ chargingPointUUID: chargingpoint.uuid, payload: autoApproveComment })
          }

          this.$emit('create', chargingpoint)


          this.handleCancelParkinglots()
          this.$emit('close')
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isSubmitting = false
      }

    },

    /**
     * handle close event of the popup
     */
    handleClose() {
      this.creationMarker?.remove()
      this.geocodedCoordinates = null

      if (!this.keepAlive) {
        this.$emit('cancel')
      }
    },

    handleCancel() {
      this.hasAddress
        ? (this.isEditing = false)
        : (this.handleClose(), this.handleCancelParkinglots())
    },

    /**
     * update the address object with data from the form
     */
    handleUpdateAddress() {
      /* update address */
      this.address.source = SOURCE.MANUAL
      this.address.street = this.fields.address.street.value
      this.address.number = String(this.fields.address.number.value)
      this.address.suffix = this.fields.address.suffix.value
      this.address.postalcode = this.fields.address.postalcode.value
      this.address.city = this.fields.address.city.value
      this.address.simple_address = formatSimpleAddress({
        address: this.address,
      })
      this.address.formatted_address = formatFormattedAddress({
        address: this.address,
      })

      /* remove CTAs, since they are not accurate anymore */
      this.creationMarker.remove()
      this.geocodedCoordinates = null

      // update ui
      this.isEditing = false
    },

    /**
     * find the anchor by calculating the bearing and placing it the other direction
     */
    setAnchor({ geocodedCoordinates }) {
      if (!(this.coordinates.length && geocodedCoordinates.length))
        return false

      this.keepAlive = true

      const bearing = turf.bearing(this.coordinates, geocodedCoordinates)
      if (bearing) {
        // if line directs downwards, place popup above
        if (bearing > -90 && bearing < 90) {
          this.anchor = 'top'
          return
        }

        // if line directs upwards, place popup beneath
        this.anchor = 'bottom'
        return
      }

      // use mapbox default (auto placement)
      this.anchor = ''
    },

    /**
     * display result accordingly
     */
    async displayResult({ geocodedCoordinates }) {
      // by setting the coordinates, the line with distance indication will have sufficient points and therefore be displayed
      this.geocodedCoordinates = geocodedCoordinates

      // Create a marker for the geocoded address to display the exact point
      this.creationMarker = await createMarker({
        coordinates: geocodedCoordinates,
        options: {
          color: getCssProperty(this.distanceIndicationBootstrapColor),
        },
        map: this.map,
      })

      await this.zoomToLine()

      this.keepAlive = false

      this.goToNextStep()
    },

    goToNextStep() {
      ++this.step
    },

    /**
     * zoom to the line so that the line to geocoded address is visible
     */
    async zoomToLine() {
      const { default: mapboxgl } = await import('mapbox-gl') // Dynamically import mapbox-gl

      const bounds = new mapboxgl.LngLatBounds(
        this.coordinates,
        this.geocodedCoordinates,
      )

      this.map.fitBounds(bounds, {
        padding: 250,
        maxZoom: 19,
      })
    },
  },
}
</script>

<style lang="scss">
.FormField label,
.FormField legend {
  font-size: 12px;
}

.parkinglot-popup {
  position: absolute;
  top: 0;
  right: 0;
  border: none;
  background: transparent;
  z-index: 1;
}

.anchor-tool {
  position: absolute;

  .b-icon {
    cursor: pointer;
    font-size: 16px;

    &.bi-arrow-down-circle,
    &.bi-arrow-down-circle-fill {
      display: block;
      margin: -0.5em 0 0 1em;
    }

    &.bi-arrow-up-circle,
    &.bi-arrow-up-circle-fill {
      margin-bottom: 0.6em;
    }
  }
}

.Popup--TwoStepGeocoding {
  .CollapsableSegment__Header {
    padding-bottom: 0.2em;
    margin-bottom: 0.6em;

    >span {
      font-size: 12px;
      font-weight: 600;
      text-align: center;
      white-space: nowrap;
    }

    .svg-inline--fa {
      height: 12px;
    }
  }
}
</style>
